import { AccountTimeZone } from "@ai-crm/agent-ui";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { adminApi } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { formatDateTime } from "../lib/format";
import { AccessTab } from "./account/access-tab";
import { AgentsTab } from "./account/agents-tab";
import { AuditTab } from "./account/audit-tab";
import { PaymentsTab } from "./account/payments-tab";
import { BillingTab } from "./account/billing-tab";
import { OverviewTab } from "./account/overview-tab";

export type AccountTab = "overview" | "payments" | "billing" | "agents" | "access" | "audit";

const accountTabs: Array<{ id: AccountTab; label: string }> = [
  { id: "overview", label: "Обзор" },
  { id: "billing", label: "Подписка и операции" },
  { id: "payments", label: "Платежи" },
  { id: "agents", label: "Агенты" },
  { id: "access", label: "Доступ" },
  { id: "audit", label: "Аудит" },
];

function isAccountTab(value: string | undefined): value is AccountTab {
  return value !== undefined && accountTabs.some((item) => item.id === value);
}

function AccountWorkspace({
  id,
  activeTab,
}: {
  id: string;
  activeTab: AccountTab;
}) {
  const { token } = useAdminAuth();
  const account = useQuery({
    queryKey: ["admin", "account", id],
    queryFn: () => adminApi.account(token!, id),
    enabled: Boolean(token),
  });

  if (account.isLoading) {
    return (
      <div className="panel p-4 text-sm text-muted-foreground">
        Загружаем аккаунт...
      </div>
    );
  }

  if (account.error || !account.data) {
    return (
      <div className="panel p-4 text-sm text-danger">
        {account.error?.message ?? "Аккаунт не найден"}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={account.data.amoAccountId}
        description={`Тариф ${account.data.tariff.name} (${account.data.tariff.id}), создан ${formatDateTime(account.data.createdAt)}`}
        backTo="/accounts"
        backLabel="К списку клиентов"
      />

      <nav
        className="mb-4 flex gap-1 overflow-x-auto border-b"
        aria-label="Разделы аккаунта"
      >
        {accountTabs.map((item) => {
          const active = item.id === activeTab;
          const href =
            item.id === "overview"
              ? `/accounts/${id}`
              : `/accounts/${id}/${item.id}`;
          return (
            <Link
              key={item.id}
              className={`shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                active
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              to={href}
              aria-current={active ? "page" : undefined}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>

      {activeTab === "overview" ? (
        <>
          <OverviewTab account={account.data} />
          <AccountTimeZone
            key={id}
            timeZone={account.data.timeZone ?? "Europe/Moscow"}
            settingsVersion={account.data.settingsVersion}
            onSave={(data) =>
              adminApi.updateAccountPreferences(token!, id, data)
            }
            onRefresh={account.refetch}
          />
        </>
      ) : null}
      {activeTab === "billing" ? (
        <BillingTab key={account.data.id} account={account.data} />
      ) : null}
      {activeTab === "payments" ? <PaymentsTab accountId={account.data.id} /> : null}
      {activeTab === "agents" ? (
        <AgentsTab accountId={account.data.id} />
      ) : null}
      {activeTab === "access" ? (
        <AccessTab key={account.data.id} account={account.data} />
      ) : null}
      {activeTab === "audit" ? <AuditTab accountId={account.data.id} /> : null}
    </div>
  );
}

export function AccountPage() {
  const { id, tab } = useParams();

  if (!id) return <Navigate to="/accounts" replace />;
  if (tab === "runs" || tab === "dialogs") {
    return <Navigate to={`/accounts/${id}/agents`} replace />;
  }
  if (tab !== undefined && !isAccountTab(tab)) {
    return <Navigate to={`/accounts/${id}`} replace />;
  }

  return <AccountWorkspace key={id} id={id} activeTab={tab ?? "overview"} />;
}
